import type { ReactNode } from "react";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import type {
  CrossCheck,
  CrossCheckMap,
  YearSummaryEmployee,
  YearSummaryMonth,
} from "../../types/payrollAdmin.js";
import type { ApprovalBlockingIssue } from "../../routes/payrollApprovalIssues.js";
import {
  aggregatePeriodCrossCheck,
  displayMismatchTooltip,
  formatComparisonPair,
  formatDisplayComparisonPair,
  formatDisplayValue,
  formatDuration,
  mismatchTooltip,
  periodDisplayValue,
  type PayrollDisplayUnit,
} from "../../routes/payrollDisplay.js";
import {
  ValueCorrectionButton,
  ValueCorrectionComparisonTargets,
  type ValueCorrectionFieldKey,
  valueCorrectionHours,
} from "./valueCorrectionFields.js";

export const crossCheckClass = (check?: CrossCheck) => {
  if (!check)
    return "inline-flex rounded-md bg-background px-1.5 py-0.5 text-foreground ring-1 ring-border";
  if (check.state === "green")
    return "inline-flex rounded-md bg-success-subtle px-1.5 py-0.5 text-success ring-1 ring-success/35";
  if (check.state === "yellow")
    return "inline-flex rounded-md bg-warning-subtle px-1.5 py-0.5 text-warning ring-1 ring-warning/45";
  return "inline-flex rounded-md bg-danger-subtle px-1.5 py-0.5 text-danger ring-1 ring-danger/45";
};

export const approvalIssueValues = (
  issue: ApprovalBlockingIssue,
  unit: PayrollDisplayUnit,
  hoursPerDay: number,
) => {
  const format = (value: number | null | undefined) => {
    if (typeof value !== "number") return "Missing";
    return formatDuration(value, unit, hoursPerDay);
  };
  const values: string[] = [];
  if (issue.timesheetValue !== undefined)
    values.push(
      `${issue.checkKey === "workload" ? "Covered" : "Timesheet"} ${format(issue.timesheetValue)}`,
    );
  if (issue.payslipValue !== undefined)
    values.push(
      `${issue.checkKey === "workload" ? "Contract" : "Payslip"} ${format(issue.payslipValue)}`,
    );
  if (issue.difference !== undefined && issue.difference !== null)
    values.push(`Difference ${format(Math.abs(issue.difference))}`);
  return values.join(" · ");
};

export const MetricCell = ({
  children,
  check,
  title,
  className = "",
  onResolve,
  resolveLabel,
  compactTarget = false,
}: {
  children: ReactNode;
  check?: CrossCheck | undefined;
  title?: string | undefined;
  className?: string | undefined;
  onResolve?: ((trigger: HTMLButtonElement) => void) | undefined;
  resolveLabel?: string | undefined;
  compactTarget?: boolean;
}) => {
  const isAcceptedEvidence = check?.accepted === true && Boolean(onResolve);
  const isActionableMismatch = check?.state === "red" && Boolean(onResolve);
  const targetFillsRow =
    isActionableMismatch || isAcceptedEvidence || compactTarget;
  return (
    <td
      className={`${targetFillsRow ? "px-1 py-0" : "px-2 py-1"} whitespace-nowrap ${className}`}
    >
      {(check?.state === "red" || check?.accepted === true) && onResolve ? (
        <button
          type="button"
          title={title}
          aria-label={resolveLabel}
          onClick={(event) => onResolve(event.currentTarget)}
          className={`${crossCheckClass(check)} payroll-reconciliation-target max-w-full cursor-pointer items-center gap-1.5 whitespace-normal transition-colors hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent`}
        >
          {check.accepted ? (
            <ShieldCheck size={13} className="shrink-0" aria-hidden="true" />
          ) : (
            <AlertTriangle size={13} className="shrink-0" aria-hidden="true" />
          )}
          <span className="min-w-0 break-words">{children}</span>
        </button>
      ) : (
        <span
          title={title}
          className={`${crossCheckClass(check)} max-w-full whitespace-normal break-words`}
        >
          {children}
        </span>
      )}
    </td>
  );
};

export const hasPaidLeaveEvidence = (row: YearSummaryMonth) =>
  Boolean(row.crossChecks?.["paidLeave"]) ||
  [
    row.paidLeaveHours,
    row.effectiveValues?.["paid_leave.timesheet"],
    row.effectiveValues?.["paid_leave.payslip"],
  ].some((value) => typeof value === "number");

export const ComparisonHeader = ({
  label,
  left = "Timesheet",
  className = "",
}: {
  label: string;
  left?: "Timesheet" | "Calculated";
  className?: string;
}) => (
  <th
    className={`break-words px-2 py-2 align-bottom font-medium leading-4 ${className}`}
  >
    <span className="block text-foreground-muted">{label}</span>
    <span className="mt-0.5 block text-xs font-normal text-foreground-muted">
      {left} | Payslip
    </span>
  </th>
);

export const ComparisonMetricCell = ({
  check,
  fallbackLeftHours,
  unit,
  hoursPerDay,
  className,
  onResolve,
  resolveLabel,
}: {
  check?: CrossCheck;
  fallbackLeftHours: number | null | undefined;
  unit: PayrollDisplayUnit;
  hoursPerDay?: number | null;
  className?: string;
  onResolve?: () => void;
  resolveLabel?: string;
}) => (
  <MetricCell
    check={check}
    title={mismatchTooltip(check, unit, hoursPerDay)}
    className={className}
    onResolve={onResolve}
    resolveLabel={resolveLabel}
  >
    {formatComparisonPair(check, fallbackLeftHours, unit, hoursPerDay)}
  </MetricCell>
);

type OpenValueCorrection = (
  fieldKey: ValueCorrectionFieldKey,
  trigger: HTMLButtonElement,
) => void;

export const ValueCorrectionMetricCell = ({
  row,
  fieldKey,
  unit,
  hoursPerDay,
  check,
  title,
  onOpen,
}: {
  row: YearSummaryMonth;
  fieldKey: ValueCorrectionFieldKey;
  unit: PayrollDisplayUnit;
  hoursPerDay: number | null;
  check?: CrossCheck;
  title?: string;
  onOpen: OpenValueCorrection;
}) => (
  <MetricCell check={check} title={title} compactTarget>
    <ValueCorrectionButton fieldKey={fieldKey} row={row} onOpen={onOpen}>
      {formatDuration(valueCorrectionHours(row, fieldKey), unit, hoursPerDay)}
    </ValueCorrectionButton>
  </MetricCell>
);

export const ValueCorrectionComparisonMetricCell = ({
  row,
  leftKey,
  rightKey,
  check,
  unit,
  hoursPerDay,
  onOpen,
}: {
  row: YearSummaryMonth;
  leftKey: ValueCorrectionFieldKey;
  rightKey: ValueCorrectionFieldKey;
  check?: CrossCheck;
  unit: PayrollDisplayUnit;
  hoursPerDay: number | null;
  onOpen: OpenValueCorrection;
}) => (
  <MetricCell
    check={check}
    title={mismatchTooltip(check, unit, hoursPerDay)}
    compactTarget
  >
    <ValueCorrectionComparisonTargets
      row={row}
      leftKey={leftKey}
      rightKey={rightKey}
      format={(valueHours) => formatDuration(valueHours, unit, hoursPerDay)}
      onOpen={onOpen}
    />
  </MetricCell>
);

export const PeriodComparisonMetricCell = ({
  rows,
  checkKey,
  valueKey,
  unit,
  className,
}: {
  rows: YearSummaryMonth[];
  checkKey: keyof CrossCheckMap;
  valueKey: keyof YearSummaryMonth;
  unit: PayrollDisplayUnit;
  className?: string;
}) => {
  const value = (row: YearSummaryMonth) =>
    typeof row[valueKey] === "number" ? (row[valueKey] as number) : null;
  const check = aggregatePeriodCrossCheck(rows, checkKey, value, unit) as
    | CrossCheck
    | undefined;
  const fallback = periodDisplayValue(rows, value, unit);
  return (
    <MetricCell
      check={check}
      title={displayMismatchTooltip(check, unit)}
      className={className}
    >
      {formatDisplayComparisonPair(check, fallback, unit)}
    </MetricCell>
  );
};

export const PeriodHoursMetricCell = ({
  rows,
  valueKey,
  unit,
}: {
  rows: YearSummaryMonth[];
  valueKey: keyof YearSummaryMonth;
  unit: PayrollDisplayUnit;
}) => (
  <MetricCell>
    {formatDisplayValue(
      periodDisplayValue(
        rows,
        (row) =>
          typeof row[valueKey] === "number"
            ? (row[valueKey] as number)
            : null,
        unit,
      ),
      unit,
    )}
  </MetricCell>
);

export const PeriodTotalMetric = ({
  label,
  value,
  check,
  title,
}: {
  label: string;
  value: ReactNode;
  check?: CrossCheck | undefined;
  title?: string | undefined;
}) => (
  <div className="min-w-0 border-l border-border/80 px-3 first:border-l-0 sm:px-4">
    <p className="min-h-8 text-xs font-medium leading-4 text-foreground-muted">
      {label}
    </p>
    <span
      title={title}
      className={`${crossCheckClass(check)} mt-2 max-w-full items-start gap-1.5 text-sm font-semibold tabular-nums`}
    >
      <span aria-hidden="true" className="text-foreground-subtle">
        ∑
      </span>
      <span className="min-w-0 break-words leading-5">{value}</span>
    </span>
  </div>
);

export const OvertimePotMetricCell = ({
  row,
  unit,
}: {
  row: YearSummaryEmployee;
  unit: PayrollDisplayUnit;
}) => {
  const check = aggregatePeriodCrossCheck(
    row.monthlyRows,
    "overtime",
    (monthRow) => monthRow.timesheetHours,
    unit,
  ) as CrossCheck | undefined;
  const value = unit === "days" ? row.overtimePotDays : row.overtimePotHours;
  return (
    <MetricCell check={check} title={displayMismatchTooltip(check, unit)}>
      {formatDisplayValue(value, unit)}
    </MetricCell>
  );
};
